import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ListBulletIcon, Squares2X2Icon } from "@heroicons/react/24/outline";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { useInvoiceBrowserView, type InvoiceBrowserView } from "@/lib/invoiceBrowserView";
import {
  INVOICE_ALLE_PAGE_SIZE,
  INVOICE_SEARCH_STATUSES,
  approvalStatusClass,
  approvalStatusLabel,
  formatDay,
  formatMoney,
  invoiceNumberFromSubject,
  listInvoiceApprovals,
  searchInvoiceDocuments,
  splitSnippet,
  type InvoiceApprovalPreview,
  type InvoiceApprovalProject,
  type InvoiceApprovalQueueItem,
  type InvoiceApprovalScope,
  type InvoiceSearchItem,
  type InvoiceSearchResult,
  type InvoiceSearchStatus,
} from "@/lib/invoiceApproval";
import { ApprovalDialog } from "@/pages/invoiceApproval/ApprovalDialog";
import { InvoiceThumb } from "@/pages/invoiceApproval/InvoiceThumb";

const SCOPES: Array<{ id: InvoiceApprovalScope; label: string }> = [
  { id: "mine", label: "Mir vorliegend" },
  { id: "offen", label: "Offen" },
  { id: "klaerung", label: "In Klärung" },
  { id: "zweite", label: "Zweite Freigabe" },
  { id: "erledigt", label: "Erledigt" },
  { id: "alle", label: "Alle" },
];

/** Ab dieser Länge sucht die Oberfläche serverseitig über alle Belege. */
const SEARCH_MIN_CHARS = 2;
const SEARCH_DEBOUNCE_MS = 250;

export function InvoiceApprovalIndexPage() {
  const { view, setView } = useInvoiceBrowserView();
  const [scope, setScope] = useState<InvoiceApprovalScope>("mine");
  const [query, setQuery] = useState("");
  const [searchStatus, setSearchStatus] = useState<InvoiceSearchStatus>("alle");
  const [items, setItems] = useState<InvoiceApprovalQueueItem[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);

  const [searchResult, setSearchResult] = useState<InvoiceSearchResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchTick, setSearchTick] = useState(0);
  const searchAbort = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listInvoiceApprovals(
        scope,
        scope === "alle" ? { limit: INVOICE_ALLE_PAGE_SIZE } : {},
      );
      const next = res.items || [];
      setItems(next);
      setTotal(typeof res.total === "number" ? res.total : next.length);
      setHasMore(Boolean(res.has_more));
      setNextOffset((res.offset ?? 0) + next.length);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Die Freigaben konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [scope]);

  const loadMore = useCallback(async () => {
    if (scope !== "alle" || !hasMore || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const res = await listInvoiceApprovals("alle", {
        offset: nextOffset,
        limit: INVOICE_ALLE_PAGE_SIZE,
      });
      const next = res.items || [];
      setItems((prev) => {
        const seen = new Set(prev.map((item) => item.candidate_id));
        return [...prev, ...next.filter((item) => !seen.has(item.candidate_id))];
      });
      if (typeof res.total === "number") setTotal(res.total);
      setHasMore(Boolean(res.has_more) && next.length > 0);
      setNextOffset((res.offset ?? nextOffset) + next.length);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Weitere Belege konnten nicht geladen werden.");
    } finally {
      setLoadingMore(false);
    }
  }, [scope, hasMore, loadingMore, nextOffset]);

  useEffect(() => {
    void load();
  }, [load]);

  const trimmedQuery = query.trim();
  const searchActive = trimmedQuery.length >= SEARCH_MIN_CHARS;

  useEffect(() => {
    if (!searchActive) {
      searchAbort.current?.abort();
      searchAbort.current = null;
      setSearchResult(null);
      setSearchError(null);
      setSearching(false);
      return;
    }
    const controller = new AbortController();
    searchAbort.current?.abort();
    searchAbort.current = controller;
    setSearching(true);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const res = await searchInvoiceDocuments(trimmedQuery, {
            status: searchStatus,
            limit: 80,
            signal: controller.signal,
          });
          if (controller.signal.aborted) return;
          setSearchResult(res);
          setSearchError(null);
        } catch (err) {
          if (controller.signal.aborted) return;
          const aborted = err instanceof DOMException && err.name === "AbortError";
          if (aborted) return;
          setSearchError(err instanceof ApiError ? err.message : "Die Belegsuche ist fehlgeschlagen.");
        } finally {
          if (!controller.signal.aborted) setSearching(false);
        }
      })();
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [searchActive, trimmedQuery, searchStatus, searchTick]);

  const visible = useMemo(() => {
    const needle = trimmedQuery.toLowerCase();
    if (!needle) return items;
    return items.filter((item) => {
      const blob = [
        item.header.supplier_name,
        item.header.invoice_number,
        item.subject,
        item.from_name,
        item.assignee_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(needle);
    });
  }, [items, trimmedQuery]);

  const browseIds = useMemo(
    () => (searchActive ? searchResult?.items || [] : visible).map((item) => item.candidate_id),
    [searchActive, searchResult, visible],
  );

  const handleChanged = useCallback(() => {
    void load();
    // Status/Zuweisung kann sich geändert haben – Ergebnisliste neu holen.
    if (searchActive) setSearchTick((tick) => tick + 1);
  }, [load, searchActive]);

  return (
    <AppShell
      title="Rechnungsfreigabe"
      subtitle="Belege prüfen, Preisabweichungen sehen und freigeben, klären oder ablehnen."
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {SCOPES.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => setScope(entry.id)}
              disabled={searchActive}
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                scope === entry.id && !searchActive
                  ? "bg-accent text-white"
                  : "border border-separator text-label-secondary hover:bg-card"
              } ${searchActive ? "opacity-50" : ""}`}
            >
              {entry.label}
            </button>
          ))}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <ViewModeSwitch value={view} onChange={setView} />
            {searchActive ? (
              <select
                className="rounded border border-separator bg-card px-2 py-1.5 text-sm"
                value={searchStatus}
                onChange={(event) => setSearchStatus(event.target.value as InvoiceSearchStatus)}
                aria-label="Belegstatus"
              >
                {INVOICE_SEARCH_STATUSES.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.label}
                  </option>
                ))}
              </select>
            ) : null}
            <input
              type="search"
              className="min-w-64 rounded border border-separator bg-card px-3 py-1.5 text-sm"
              placeholder="Alle Belege durchsuchen: Lieferant, Nummer, Artikel, Betrag, Projekt …"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label="Belegsuche"
            />
          </div>
        </div>

        {searchActive ? (
          <SearchResults
            query={trimmedQuery}
            view={view}
            result={searchResult}
            searching={searching}
            error={searchError}
            onOpen={setOpenId}
          />
        ) : (
          <>
            {error ? <Alert variant="error">{error}</Alert> : null}
            {loading ? (
              <div className="flex justify-center py-16">
                <Spinner />
              </div>
            ) : visible.length === 0 ? (
              <p className="rounded-xl border border-separator bg-card px-4 py-10 text-center text-sm text-label-secondary">
                {scope === "alle" && !trimmedQuery
                  ? "Keine Belege vorhanden."
                  : "Keine Rechnungen in dieser Ansicht."}
              </p>
            ) : (
              <div className="space-y-4">
                <BelegCollection view={view} items={visible.map(queueToView)} onOpen={setOpenId} />
                {scope === "alle" && !trimmedQuery && (hasMore || items.length < total) ? (
                  <div className="flex flex-col items-center gap-2">
                    <p className="text-xs text-label-secondary">
                      {Math.min(items.length, total)} von {total} Belegen
                    </p>
                    {hasMore ? (
                      <button
                        type="button"
                        onClick={() => void loadMore()}
                        disabled={loadingMore}
                        className="rounded-full border border-separator px-3 py-1 text-xs font-medium text-label-secondary hover:bg-card disabled:opacity-50"
                      >
                        {loadingMore ? "Wird geladen …" : "Weitere Belege laden"}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
          </>
        )}
      </div>

      <ApprovalDialog
        candidateId={openId}
        browseIds={browseIds}
        onBrowse={setOpenId}
        onClose={() => setOpenId(null)}
        onChanged={handleChanged}
      />
    </AppShell>
  );
}

interface BelegView {
  candidateId: number;
  supplier: string;
  numberLabel: string;
  status: string;
  amount: number | null | undefined;
  currency: string;
  date: string | null | undefined;
  projectCount: number;
  preview?: InvoiceApprovalPreview | null;
  assigneeName?: string | null;
  filed: boolean;
  priceDeviationCount: number;
  duplicate: boolean;
  skonto: boolean;
  projects: InvoiceApprovalProject[];
  matchReasons: string[];
  snippet: string | null;
  subject: string | null;
}

function numberLabel(invoiceNumber: string | null | undefined, subject: string | null | undefined): string {
  return invoiceNumber || invoiceNumberFromSubject(subject) || subject || "ohne Nummer";
}

function queueToView(item: InvoiceApprovalQueueItem): BelegView {
  return {
    candidateId: item.candidate_id,
    supplier: item.header.supplier_name || item.from_name || "Unbekannter Absender",
    numberLabel: numberLabel(item.header.invoice_number, item.subject),
    status: item.status,
    amount: item.header.total_gross,
    currency: item.header.currency || "EUR",
    date: item.header.invoice_date || item.received_at,
    projectCount: item.project_count,
    preview: item.preview,
    assigneeName: item.assignee_name,
    filed: item.filed_count > 0,
    priceDeviationCount: item.price_deviation_count,
    duplicate: item.duplicate,
    skonto: Boolean(item.header.skonto),
    projects: [],
    matchReasons: [],
    snippet: null,
    subject: item.subject ?? null,
  };
}

function searchToView(item: InvoiceSearchItem): BelegView {
  return {
    candidateId: item.candidate_id,
    supplier: item.header.supplier_name || item.from_name || "Unbekannter Absender",
    numberLabel: numberLabel(item.header.invoice_number, item.subject),
    status: item.approval_status || "ohne_freigabe",
    amount: item.header.total_gross,
    currency: item.header.currency || "EUR",
    date: item.header.invoice_date || item.received_at,
    projectCount: item.project_count,
    preview: item.preview,
    assigneeName: item.assignee_name,
    filed: item.filed_count > 0,
    priceDeviationCount: 0,
    duplicate: false,
    skonto: Boolean(item.header.skonto),
    projects: item.projects || [],
    matchReasons: item.match_reasons || [],
    snippet: item.snippet ?? null,
    subject: item.subject ?? null,
  };
}

function ViewModeSwitch({
  value,
  onChange,
}: {
  value: InvoiceBrowserView;
  onChange: (next: InvoiceBrowserView) => void;
}) {
  const options: Array<{ id: InvoiceBrowserView; label: string; icon: typeof Squares2X2Icon }> = [
    { id: "kacheln", label: "Kacheln", icon: Squares2X2Icon },
    { id: "liste", label: "Liste", icon: ListBulletIcon },
  ];
  return (
    <div className="inline-flex rounded-full border border-separator bg-card p-0.5" role="group" aria-label="Ansicht">
      {options.map((option) => {
        const Icon = option.icon;
        const active = value === option.id;
        return (
          <button
            key={option.id}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.id)}
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
              active ? "bg-accent text-white" : "text-label-secondary hover:bg-fill-tertiary"
            }`}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function BelegCollection({
  view,
  items,
  onOpen,
}: {
  view: InvoiceBrowserView;
  items: BelegView[];
  onOpen: (id: number) => void;
}) {
  if (view === "liste") {
    return (
      <ul className="divide-y divide-separator overflow-hidden rounded-xl border border-separator bg-card">
        {items.map((item) => (
          <BelegRow key={item.candidateId} item={item} onOpen={onOpen} />
        ))}
      </ul>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
      {items.map((item) => (
        <BelegTile key={item.candidateId} item={item} onOpen={onOpen} />
      ))}
    </div>
  );
}

function BelegTile({ item, onOpen }: { item: BelegView; onOpen: (id: number) => void }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(item.candidateId)}
      className="overflow-hidden rounded-xl border border-separator bg-card text-left shadow-sm transition hover:border-accent/40"
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-white">
        <InvoiceThumb candidateId={item.candidateId} preview={item.preview} label={item.supplier} />
        <span className={`absolute right-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-semibold ${approvalStatusClass(item.status)}`}>
          {approvalStatusLabel(item.status)}
        </span>
        <div className="absolute left-2 top-2 flex flex-col gap-1">
          {item.priceDeviationCount > 0 ? (
            <span className="rounded-full bg-status-red px-2 py-0.5 text-[10px] font-semibold text-white">
              Preis {item.priceDeviationCount}
            </span>
          ) : null}
          {item.duplicate ? (
            <span className="rounded-full bg-status-orange px-2 py-0.5 text-[10px] font-semibold text-white">Dublette</span>
          ) : null}
          {item.skonto ? (
            <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold text-white">Skonto</span>
          ) : null}
        </div>
      </div>
      <div className="space-y-0.5 px-2.5 py-2">
        <div className="truncate text-sm font-medium">{item.supplier}</div>
        <div className="truncate font-mono text-[11px] text-label-secondary">{item.numberLabel}</div>
        <div className="text-sm font-semibold tabular-nums">{formatMoney(item.amount, item.currency)}</div>
        <div className="text-[11px] text-label-tertiary">
          {formatDay(item.date)}
          {item.projectCount > 0 ? ` · ${item.projectCount} Projekt${item.projectCount === 1 ? "" : "e"}` : ""}
        </div>
      </div>
    </button>
  );
}

function SearchResults({
  query,
  view,
  result,
  searching,
  error,
  onOpen,
}: {
  query: string;
  view: InvoiceBrowserView;
  result: InvoiceSearchResult | null;
  searching: boolean;
  error: string | null;
  onOpen: (id: number) => void;
}) {
  const items = result?.items || [];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-label-secondary">
        {searching ? <Spinner className="h-3 w-3" /> : null}
        <span>
          {searching && !result
            ? "Belege werden durchsucht …"
            : result
              ? `${result.total} Beleg${result.total === 1 ? "" : "e"} zu „${query}“`
              : ""}
        </span>
        {result?.semantik ? <span className="rounded-full bg-fill-tertiary px-2 py-0.5">inkl. inhaltlicher Ähnlichkeit</span> : null}
      </div>

      {error ? <Alert variant="error">{error}</Alert> : null}

      {result && items.length === 0 && !searching ? (
        <p className="rounded-xl border border-separator bg-card px-4 py-10 text-center text-sm text-label-secondary">
          Kein Beleg passt zu „{query}“. Tipp: Rechnungsnummer, Lieferant, Artikelbezeichnung, Betrag (z.&nbsp;B. 1.234,56) oder
          Datum (12.03.2026) eingeben.
        </p>
      ) : null}

      {items.length > 0 ? (
        <BelegCollection view={view} items={items.map(searchToView)} onOpen={onOpen} />
      ) : null}
    </div>
  );
}

function BelegRow({ item, onOpen }: { item: BelegView; onOpen: (id: number) => void }) {
  const snippet = splitSnippet(item.snippet);
  const projects = item.projects.filter((project) => project.auftragsnummer || project.beschreibung || project.kunde);
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(item.candidateId)}
        className="flex w-full items-stretch gap-3 px-3 py-2.5 text-left transition hover:bg-fill-tertiary/40 sm:px-4"
      >
        <div className="hidden w-14 shrink-0 overflow-hidden rounded border border-separator bg-white sm:block">
          <div className="aspect-[3/4]">
            <InvoiceThumb candidateId={item.candidateId} preview={item.preview} label={item.supplier} />
          </div>
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="truncate text-sm font-medium">{item.supplier}</span>
            <span className="font-mono text-[11px] text-label-secondary">{item.numberLabel}</span>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${approvalStatusClass(item.status)}`}>
              {approvalStatusLabel(item.status)}
            </span>
            {item.priceDeviationCount > 0 ? (
              <span className="rounded-full bg-status-red px-2 py-0.5 text-[10px] font-semibold text-white">
                Preis {item.priceDeviationCount}
              </span>
            ) : null}
            {item.duplicate ? (
              <span className="rounded-full bg-status-orange px-2 py-0.5 text-[10px] font-semibold text-white">Dublette</span>
            ) : null}
            {item.skonto ? (
              <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold text-white">Skonto</span>
            ) : null}
            {item.matchReasons.map((reason) => (
              <span key={reason} className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[10px] text-label-secondary">
                {reason}
              </span>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 text-xs text-label-secondary">
            <span className="font-semibold tabular-nums text-label">{formatMoney(item.amount, item.currency)}</span>
            <span>{formatDay(item.date)}</span>
            {item.assigneeName ? <span>bei {item.assigneeName}</span> : null}
            {item.filed ? <span>abgelegt</span> : null}
            {projects.length === 0 && item.projectCount > 0 ? (
              <span>
                {item.projectCount} Projekt{item.projectCount === 1 ? "" : "e"}
              </span>
            ) : null}
          </div>
          {projects.length > 0 ? (
            <div className="flex flex-wrap gap-1.5 text-[11px]">
              {projects.slice(0, 3).map((project, index) => (
                <span
                  key={`${project.auftrag_id ?? project.auftragsnummer ?? index}`}
                  className="rounded border border-separator px-1.5 py-0.5 text-label-secondary"
                >
                  {project.auftragsnummer ? `${project.auftragsnummer} · ` : ""}
                  {project.beschreibung || project.kunde || "Projekt"}
                </span>
              ))}
              {projects.length > 3 ? <span className="text-label-tertiary">+{projects.length - 3}</span> : null}
            </div>
          ) : null}
          {snippet.length > 0 ? (
            <p className="line-clamp-2 text-xs text-label-tertiary">
              {snippet.map((part, index) =>
                part.hit ? (
                  <mark key={index} className="rounded bg-accent/20 px-0.5 text-label">
                    {part.text}
                  </mark>
                ) : (
                  <span key={index}>{part.text}</span>
                ),
              )}
            </p>
          ) : item.subject && item.subject !== item.numberLabel ? (
            <p className="line-clamp-1 text-xs text-label-tertiary">{item.subject}</p>
          ) : null}
        </div>
      </button>
    </li>
  );
}
